/**
 * Motion to Withdraw as Surety (Lloyd, Aug 25 2026).
 *
 * > *"After a case is appealed, I apparently need a motion to withdraw as surety, since I sent a
 * > surety bond. Can you insert and put this step in the right place?"*
 *
 * Same two-call split as the appeal bond and the continuance motion: `readiness` asks what is
 * missing WITHOUT producing anything, and only `generate` produces a document.
 *
 * **What this document asks a court for is a warrant of arrest for the firm's own client**, so that
 * the surety may surrender them. Nothing in the system produces it automatically and nothing sends
 * it — the PDF comes back to the screen for a wet signature and a notary. Any future caller here
 * should keep it that way.
 */

export type WithdrawalGround =
  | 'FAILED_TO_APPEAR'
  | 'UNPAID_SURETY_FEE'
  | 'ATTORNEY_WITHDRAWING'
  | 'CANNOT_LOCATE';

/**
 * The four grounds, in the order Lloyd's own template lists them, with the extra fact each one has
 * to state. The picker asks for exactly that field and no others, because the motion prints one
 * reason rather than four with a tick beside one.
 */
export const WITHDRAWAL_GROUND_OPTIONS: Array<{
  value: WithdrawalGround;
  label: string;
  /** What the sentence needs beyond the case facts. */
  requires: 'missedSettingDate' | 'amounts' | 'locateEfforts' | 'none';
}> = [
  {
    value: 'FAILED_TO_APPEAR',
    label: 'Defendant failed to appear in court',
    requires: 'missedSettingDate',
  },
  {
    value: 'UNPAID_SURETY_FEE',
    label: 'Defendant has not paid the surety the amount agreed',
    requires: 'amounts',
  },
  {
    value: 'ATTORNEY_WITHDRAWING',
    label: 'The attorney is withdrawing from the representation',
    requires: 'none',
  },
  {
    value: 'CANNOT_LOCATE',
    label: 'Surety cannot locate the defendant',
    requires: 'locateEfforts',
  },
];

export interface WithdrawalBlockerDto {
  field: string | null;
  reason: string;
}

export interface WithdrawalReadinessDto {
  canGenerate: boolean;
  blockers: WithdrawalBlockerDto[];
}

export interface WithdrawalReadinessResponseDto {
  readiness: WithdrawalReadinessDto;
  liveSuretyBonds: Array<{ guid?: string; type: string }>;
}

export interface GenerateWithdrawalInput {
  ground: WithdrawalGround | string;
  /** Required where the case carries more than one live bond — a motion against the wrong one releases nobody. */
  bondGuid?: string | null;
  offense?: string | null;
  bondDate?: string | null;
  missedSettingDate?: string | null;
  amountPaidCents?: number | null;
  amountDueCents?: number | null;
  locateEffortsDescription?: string | null;
  signatureDate?: string | null;
  /** The court it is filed in, where the case has left the court on file (i.e. after an appeal). */
  courtName?: string | null;
}

export interface WithdrawalResultDto {
  fileName: string;
  pdfBase64: string;
  /** The bond the motion was recorded against, or null where none could be identified. */
  bondGuid: string | null;
}

/** Thrown when the case cannot support an honest motion. Carries the reasons so the caller can list them. */
export class SuretyWithdrawalBlockedError extends Error {
  constructor(
    message: string,
    public readonly blockers: WithdrawalBlockerDto[],
  ) {
    super(message);
    this.name = 'SuretyWithdrawalBlockedError';
  }
}

export default class SuretyWithdrawalApi {
  private static host(): string {
    return process.env.API_HOST?.trim() || window.location.origin;
  }

  static async readiness(
    ticketGuid: string,
    params: { ground?: string } = {},
  ): Promise<WithdrawalReadinessResponseDto> {
    const query = new URLSearchParams();
    if (params.ground) query.set('ground', params.ground);
    const queryString = query.toString();
    const response = await fetch(
      `${SuretyWithdrawalApi.host()}/api/v1/manager/ticket/${ticketGuid}/surety-withdrawal/readiness${
        queryString ? `?${queryString}` : ''
      }`,
      { credentials: 'include' },
    );
    if (!response.ok) {
      throw new Error('Failed to check whether this case is ready for a motion to withdraw');
    }
    return response.json();
  }

  static async generate(
    ticketGuid: string,
    input: GenerateWithdrawalInput,
  ): Promise<WithdrawalResultDto> {
    const response = await fetch(
      `${SuretyWithdrawalApi.host()}/api/v1/manager/ticket/${ticketGuid}/surety-withdrawal`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      },
    );

    // 400 here is the "which bond?" answer, not a validation slip, so it carries the same shape.
    if (response.status === 422 || response.status === 400) {
      const err = await response.json().catch(() => ({}));
      throw new SuretyWithdrawalBlockedError(
        err.message || 'This case is not ready for a motion to withdraw as surety.',
        Array.isArray(err.blockers) ? err.blockers : [],
      );
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(
        err.details || err.error || err.message || 'Failed to generate the motion to withdraw as surety',
      );
    }
    return response.json();
  }
}
